"use client";

import { LiteBatcher } from "@tanstack/pacer-lite";
import ky from "ky";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { discoveryConfig } from "./config";

export type ImpressionMetadata = {
  entityType: "person" | "hiive" | "event" | "venue" | "class";
  entityId: string;
  surface: string;
  token?: string;
};
type Delivery = ImpressionMetadata & { exposureId: string; token: string };
const recent = new Map<
  string,
  { timestamp: number; entityId: string; surface: string }
>();
const batches: Delivery[][] = [];
let delivering = false;
let unloading = false;
const batcher = new LiteBatcher<Delivery>(
  (items) => {
    // At most 100 queued records plus one in-flight batch, even offline.
    if (batches.length >= 5) batches.shift();
    batches.push(items);
    void drain();
  },
  {
    maxSize: discoveryConfig.exposureBatchSize,
    wait: discoveryConfig.exposureFlushMs,
  },
);

async function drain() {
  if (delivering) return;
  delivering = true;
  try {
    while (batches.length) {
      const impressions = batches.shift();
      try {
        await ky.post("/api/discovery/exposures", {
          json: { impressions },
          keepalive: true,
          timeout: 4000,
          retry: unloading
            ? 0
            : {
                limit: 2,
                methods: ["post"],
                statusCodes: [408, 429, 500, 502, 503, 504],
              },
        });
      } catch {
        // Ephemeral freshness may be lost. Never block a card or analytics.
      }
    }
  } finally {
    delivering = false;
  }
}

export function flushDiscoveryImpressions() {
  unloading = true;
  batcher.flush();
  // If a request is in flight, send waiting records separately on pagehide.
  const pending = batches.splice(0).flat();
  for (
    let index = 0;
    index < pending.length;
    index += discoveryConfig.exposureBatchSize
  ) {
    void ky
      .post("/api/discovery/exposures", {
        json: {
          impressions: pending.slice(
            index,
            index + discoveryConfig.exposureBatchSize,
          ),
        },
        keepalive: true,
        timeout: 4000,
        retry: 0,
      })
      .catch(() => {});
  }
}

export function recordQualifiedImpression(
  metadata: ImpressionMetadata,
): boolean {
  // Tokens identify a delivered candidate set. Same payload on back navigation
  // and StrictMode remounts does not emit another qualified impression.
  const key = `${metadata.surface}:${metadata.entityType}:${metadata.entityId}:${metadata.token ?? "guest"}`;
  unloading = false;
  const now = Date.now();
  const previous = recent.get(key);
  if (previous !== undefined && now - previous.timestamp < 30 * 60 * 1000)
    return false;
  recent.delete(key);
  recent.set(key, {
    timestamp: now,
    entityId: metadata.entityId,
    surface: metadata.surface,
  });
  while (recent.size > 500) recent.delete(recent.keys().next().value as string);
  const exposureId = crypto.randomUUID();
  try {
    // PostHog's SDK owns its own background queue/batching.
    captureClientEvent(ANALYTICS_EVENTS.DISCOVERY_IMPRESSION, {
      entity_type: metadata.entityType,
      entity_id: metadata.entityId,
      surface: metadata.surface,
      exposure_id: exposureId,
      config_version: discoveryConfig.version,
      visible_ratio: discoveryConfig.impressionVisibleRatio,
      dwell_ms: discoveryConfig.impressionDwellMs,
    });
  } catch {
    /* Analytics failure does not prevent independent Redis delivery. */
  }
  if (metadata.token) {
    batcher.addItem({
      ...metadata,
      token: metadata.token,
      exposureId,
    });
  }
  return true;
}

export function recentlyQualifiedIds(surface: string): string[] {
  return [
    ...new Set(
      [...recent.values()]
        .filter(
          (value) =>
            value.surface === surface &&
            Date.now() - value.timestamp < 30 * 60 * 1000,
        )
        .map((value) => value.entityId),
    ),
  ].slice(-100);
}
